import React, { useState, useEffect } from 'react'
import { Form, Button, Card, Table, Alert } from 'react-bootstrap';
import api from '../services/api';

const LeadMappingSubmit = () => {
    const [billingMonths, setBillingMonths] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState('');
    const [lenders, setLenders] = useState([]);
    const [selectedLender, setSelectedLender] = useState('');
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState('');
    const [connectors, setConnectors] = useState([]);
    const [selectedConnector, setSelectedConnector] = useState('');
    const [summary, setSummary] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        // Fetch billing months, lenders, connectors, and products
        const fetchData = async () => {
            try {
                const billingMonthsResponse = await api.get('/api/billing-months/');
                setBillingMonths(billingMonthsResponse.data);

                const lendersResponse = await api.get('/api/lenders/');
                setLenders(lendersResponse.data);

                const connectorsResponse = await api.get('/api/connectors/');
                setConnectors(connectorsResponse.data);

                const productsResponse = await api.get('/api/products/');
                setProducts(productsResponse.data);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);

    const handleFetchSummary = () => {
        setErrorMessage('');

        // Fetch the summary data for the selected connector
        api.get(`/api/file-summary-for-submission/${selectedConnector}/`, {
            params: {
                billing_month: selectedMonth,
                lender: selectedLender,
                product: selectedProduct
            }
        })
            .then(response => setSummary(response.data))
            .catch(error => {
                if (error.response?.data?.error) {
                    setErrorMessage(error.response.data.error);
                } else {
                    console.error('Error fetching summary:', error);
                }
            });
    };

    const handleSubmitMapping = () => {
        // Submit the mapped leads for the selected connector
        api.post(`/api/submit-mapped-leads/${selectedConnector}/`)
            .then(response => {
                alert('Mapped leads submitted successfully');
                resetUI();
            })
            .catch(error => {
                if (error.response?.data?.error) {
                    setErrorMessage(error.response.data.error);
                } else {
                    console.error('Error submitting mapped leads:', error);
                }
            });
    };

    const resetUI = () => {
        setSummary(null);
        setErrorMessage('');
        setSelectedMonth('');
        setSelectedLender('');
        setSelectedConnector('');
        setSelectedProduct('');
    };

    return (
        <Card>
            <Card.Body>
                <Card.Title>Submit Payout Mapping File</Card.Title>
                <Form>
                    <Form.Group className='mt-4'>
                        <Form.Label>
                            <span style={{ color: 'red', fontSize: '1.2em' }}>*</span> Select Billing Month
                        </Form.Label>
                        <Form.Control as="select" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                            <option value="">Select Billing Month</option>
                            <option value="all">Select All</option>
                            {billingMonths
                                .sort((a, b) => {
                                    const getMonthValue = (month) => {
                                        const monthOrder = {
                                            January: 10,
                                            February: 11,
                                            March: 12,
                                            April: 1,
                                            May: 2,
                                            June: 3,
                                            July: 4,
                                            August: 5,
                                            September: 6,
                                            October: 7,
                                            November: 8,
                                            December: 9,
                                        };
                                        return monthOrder[month];
                                    };

                                    const aMonthValue = getMonthValue(a.month);
                                    const bMonthValue = getMonthValue(b.month);

                                    if (a.year === b.year) {
                                        return aMonthValue - bMonthValue;
                                    }
                                    return a.year - b.year;
                                })
                                .map(month => (
                                    <option key={month.id} value={month.id}>{`${month.month} ${month.year}`}</option>
                                ))}
                        </Form.Control>
                    </Form.Group>
                    <Form.Group className='mt-2'>
                        <Form.Label> Select Lender </Form.Label>
                        <Form.Control as="select" value={selectedLender} onChange={(e) => setSelectedLender(e.target.value)}>
                            <option value="">Select Lender</option>
                            {lenders.map(lender => (
                                <option key={lender.id} value={lender.id}>{lender.name}</option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                    <Form.Group className='mt-2'>
                        <Form.Label>
                            <span style={{ color: 'red', fontSize: '1.2em' }}>*</span> Select Connector
                        </Form.Label>
                        {/* <Form.Control as="select" value={selectedConnector} onChange={(e) => setSelectedConnector(e.target.value)}>
                            <option value="">Select Connector</option>
                            {connectors.map(connector => (
                                <option key={connector.name} value={connector.name}>{connector.name}</option>
                            ))}
                        </Form.Control> */}
                        <Form.Control as="select" value={selectedConnector} onChange={(e) => setSelectedConnector(e.target.value)}>
                            <option value="">Select Connector</option>
                            {connectors
                                .filter(connector => connector.status?.toLowerCase() !== 'deleted') // ✅ filter out deleted connectors
                                .map(connector => (
                                    <option key={connector.name} value={connector.name}>
                                        {connector.name}
                                    </option>
                                ))}
                        </Form.Control>
                    </Form.Group>
                    <Form.Group className='mt-2'>
                        <Form.Label>Select Product</Form.Label>
                        <Form.Control as="select" value={selectedProduct} onChange={(e) => setSelectedProduct(e.target.value)}>
                            <option value="">Select Product</option>
                            {products.map(product => (
                                <option key={product.id} value={product.id}>{product.product_name}</option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                    <Button className='mt-4' variant="primary" onClick={handleFetchSummary}>Proceed to Fetch Summary</Button>
                </Form>

                {errorMessage && (
                    <Alert variant="danger" className="mt-4">
                        {errorMessage}
                    </Alert>
                )}

                {summary && (
                    <Card className="mt-3">
                        <Card.Body>
                            <Card.Title>Summary</Card.Title>
                            <Table striped bordered hover>
                                <tbody>
                                    <tr>
                                        <th>Connector Name</th>
                                        <td>{summary.connector_name}</td>
                                    </tr>
                                    <tr>
                                        <th>Total Number of Leads</th>
                                        <td>{summary.mapped_leads_count}</td>
                                    </tr>
                                    <tr>
                                        <th>Total Loan Amount</th>
                                        <td>{summary.total_loan_amount}</td>
                                    </tr>
                                    <tr>
                                        <th>Commission Paid</th>
                                        <td>{summary.commission_paid}</td>
                                    </tr>
                                </tbody>
                            </Table>
                            <Button variant="success" onClick={handleSubmitMapping}>Submit Mapped Leads</Button>
                        </Card.Body>
                    </Card>
                )}
            </Card.Body>
        </Card>
    );
};

export default LeadMappingSubmit;